// src/services/api.ts

import axios from 'axios';

declare module 'axios' {
  interface AxiosRequestConfig {
    // Background calls the user did not trigger; kept out of the on-screen
    // response-time indicator.
    skipPerfMetric?: boolean;
  }
}

const API_BASE_URL = `${import.meta.env.VITE_API_URL ?? 'http://localhost:5001'}/api`;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export { api };

// ═══════════════════════════════════════════════════════════
// Pagination Types with Filtering
// ═══════════════════════════════════════════════════════════

export interface PaginatedResult<T> {
  data: T[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface PaginationParams {
  pageNumber?: number;
  pageSize?: number;
  letterFilter?: string;
  searchText?: string;
  clientIds?: number[];
  hasPets?: boolean;
  hasBookings?: boolean;
  hasOverdueInvoices?: boolean;
  hasOutstandingBalance?: boolean;
  stillOwned?: boolean;
  petTypeIds?: number[];
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  clientId?: number;
  status?: string;
  startDate?: string;
  endDate?: string;
  isProspect?: boolean;
  isExpired?: boolean;
  openId?: number;
}

export interface DashboardStats {
  activeBookings: number;
  checkInsToday: number;
  checkOutsToday: number;
  availableKennels: number;
  overdueInvoices: number;
}

// ═══════════════════════════════════════════════════════════
// Performance Metrics Types
// ═══════════════════════════════════════════════════════════

export interface PerformanceMetric {
  id: number;
  endpoint: string;
  responseTimeMs: number;
  statusCode?: number;
  timestamp: string;
}

export interface PerformanceMetricsResponse {
  success: boolean;
  data: PerformanceMetric[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface EndpointStats {
  endpoint: string;
  count: number;
  averageResponseTime: number;
  minResponseTime: number;
  maxResponseTime: number;
}

export interface PerformanceStats {
  totalRequests: number;
  averageResponseTime: number;
  minResponseTime: number;
  maxResponseTime: number;
  slowRequests: number;
  endpointBreakdown: Record<string, EndpointStats>;
}

export interface PerformanceStatsResponse {
  success: boolean;
  data: PerformanceStats;
}

// ═══════════════════════════════════════════════════════════
// Existing Interfaces
// ═══════════════════════════════════════════════════════════

export interface Client {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  vetId?: number;
  vet?: Vet;
  idNumber: string;
  alternateContact: string;
  alternateContactOther: string;
  notes: string;
  showWarning?: boolean;
  isProspect?: boolean;
  createdFromQuoteId?: number;
  createdFromQuoteConvertedAt?: string;
  createdAt?: string;
  updatedAt?: string;
  createdByUsername?: string;
  updatedByUsername?: string;
  isDeleted?: boolean;
  pets?: Pet[];
  rowVersion?: string;
  rowVersionString?: string;
}

export interface Pet {
  id: number;
  name: string;
  clientId: number;
  client?: Client;
  petTypeId: number;
  petType?: PetType;
  petSizeId: number;
  petSize?: PetSize;
  petBreedId?: number;
  petBreed?: PetBreed;
  petColourId?: number;
  petColour?: PetColour;
  dateOfBirth?: string;
  gender: string;
  notes?: string;
  stillOwned?: boolean;
  showWarning: boolean;
  sterilised?: boolean;
  inoculationDate?: string;
  isSketch?: boolean;
  createdAt?: string;
  updatedAt?: string;
  createdByUsername?: string;
  updatedByUsername?: string;
  isDeleted?: boolean;
  siblings?: Pet[];
  bookings?: Booking[];
  rowVersion?: string;
  rowVersionString?: string;
}

export interface PetFormData {
  name: string;
  clientId: number;
  petTypeId: string;
  petSizeId: string;
  petBreedId: string;
  petColourId: string;
  dateOfBirth: string;
  gender: string;
  notes: string;
  stillOwned: boolean;
  showWarning: boolean;
  inoculationDate: string;
  sterilised: boolean;
}

export interface QuoteProspectInput {
  firstName: string;
  lastName: string;
  phone?: string;
  email?: string;
}

export interface QuoteSketchPetInput {
  petTypeId: number;
  petSizeId: number;
  petBreedId?: number;
  petName?: string;
  rateType?: 'Single' | 'Sharing' | 'Custom';
  customRate?: number;
  useSharingRate?: boolean;
  numberOfNights?: number;
  groomingTypes?: string[];
  lineItemDiscountPercentage?: number;
  lineItemDiscount?: number;
}

export interface QuoteRequest {
  clientId?: number;
  prospect?: QuoteProspectInput;
  checkInDate: string;
  checkOutDate: string;
  existingPetIds?: number[];
  pets?: QuoteSketchPetInput[];
  notes?: string;
  quoteExpiresAt?: string;
  heaterNights?: number;
  surcharges?: QuoteDetailedSurchargeInput[];
}

export interface QuoteResponse {
  bookingId: number;
  clientId: number;
  clientWasCreated: boolean;
  petIds: number[];
  quoteExpiresAt: string;
}

export interface QuoteDetailedPetInput {
  petId: number;
  kennelId?: number | null;
  useSharingRate?: boolean;
  rateType?: string;
  customRate?: number;
  numberOfNights?: number;
  lineItemDiscountPercentage?: number;
  lineItemDiscount?: number;
  groomingTypes: string[];
}

export interface QuoteDetailedSurchargeInput {
  reason: string;
  amount: number;
}

export interface QuoteDetailedRequest {
  clientId: number;
  checkInDate: string;
  checkOutDate: string;
  pets: QuoteDetailedPetInput[];
  surcharges?: QuoteDetailedSurchargeInput[];
  heaterNights?: number;
  notes?: string;
  quoteExpiresAt?: string;
}

export interface ConvertSketchPetEntry {
  petId: number;
  name: string;

  // Prospect-only extended fields. All optional on the wire so the simple
  // existing-client convert path can keep sending just { petId, name }.
  // The backend enforces petTypeId, petSizeId, gender, sterilised and
  // stillOwned as required when the quote's client is a prospect.
  petTypeId?: number;
  petSizeId?: number;
  petBreedId?: number | null;
  petColourId?: number | null;
  gender?: string;
  dateOfBirth?: string | null; // ISO yyyy-MM-dd
  inoculationDate?: string | null; // ISO yyyy-MM-dd
  sterilised?: boolean;
  stillOwned?: boolean;
}

// Prospect-only payload sent on the convert request to fill in the gaps
// in client data captured at quote time. Required fields (when prospect)
// are firstName, lastName, phone. All other fields are optional and the
// backend skips encrypting/persisting blank values.
export interface ConvertClientPatch {
  firstName: string;
  lastName: string;
  phone: string;
  email?: string;
  address?: string;
  vetId?: number | null;
  idNumber?: string;
  alternateContact?: string;
  alternateContactOther?: string;
}

// Prospect-only payload. One entry per kennel row the operator has added
// in the convert dialog. KennelId is the specific kennel (not a side
// placeholder); heaterNights defaults to 0.
export interface ConvertKennelEntry {
  kennelId: number;
  heaterNights: number;
}

export interface ConvertQuoteRequest {
  pets: ConvertSketchPetEntry[];

  // Prospect-only. Backend requires this when the quote's client is a
  // prospect; ignored on existing-client conversions.
  clientPatch?: ConvertClientPatch;

  // Prospect-only. Backend requires at least one entry when the quote's
  // client is a prospect; ignored on existing-client conversions, which
  // continue to defer kennel assignment to the standalone booking edit
  // flow.
  kennels?: ConvertKennelEntry[];
}

export interface ConvertQuoteResponse {
  // Id of the retained quote (same id passed to the convert endpoint).
  bookingId: number;
  // Id of the new "Pending" booking; navigate here on success.
  newBookingId: number;
  clientId: number;
  clientWasProspect: boolean;
  sketchPetsRenamed: number;
}

export interface PetType {
  id: number;
  type: string;
  name?: string;
  active?: boolean;
  isDeleted?: boolean;
  deletedAt?: string;
  // True for every seeded/standard item - read-only, never sent in a
  // create/update payload. Name is locked for these; Active still isn't.
  isSystem?: boolean;
}

export interface PetSize {
  id: number;
  size: string;
  petTypeFk: number;
  petType?: PetType;
  priceSingle?: number;
  priceSharing?: number;
  priceGroomingSmooth?: number;
  priceGroomingLong?: number;
  priceCut?: number;
  active?: boolean;
  isDeleted?: boolean;
  deletedAt?: string;
  // True for every seeded/standard item - read-only, never sent in a
  // create/update payload. Name/parent Type are locked for these; Active
  // and the price fields still aren't.
  isSystem?: boolean;
}

export interface PetBreed {
  id: number;
  breed: string;
  petType: number;
  petTypeNavigation?: PetType;
  petSizeFk: number;
  petSize?: PetSize;
  active?: boolean;
  isDeleted?: boolean;
  deletedAt?: string;
  // True for every seeded/standard item - read-only, never sent in a
  // create/update payload. Name/parent Type/Size are locked for these;
  // Active still isn't.
  isSystem?: boolean;
}

export interface PetColour {
  id: number;
  colour: string;
  // NB: the backend model (PetColour.cs) exposes this FK as `PetType`
  // (int), not `PetTypeFk` like PetSize/PetBreed - it serializes as
  // `petType`, not `petTypeFk`. Kept as `petType` here to match the
  // real wire shape.
  petType: number;
  petTypeNavigation?: PetType;
  active?: boolean;
  isDeleted?: boolean;
  deletedAt?: string;
  // True for every seeded/standard item - read-only, never sent in a
  // create/update payload. Name/parent Type are locked for these; Active
  // still isn't.
  isSystem?: boolean;
}

export interface PetReferenceUsage {
  petTypes: Record<number, number>;
  petSizes: Record<number, number>;
  petBreeds: Record<number, number>;
  petColours: Record<number, number>;
}

export interface KennelReferenceUsage {
  kennels: Record<number, number>;
  sides: Record<number, number>;
  sizes: Record<number, number>;
}

export interface KennelSize {
  id: number;
  name: string;
  sortOrder: number;
  kennelCount?: number;
  isDeleted?: boolean;
  deletedAt?: string;
  // True for every seeded/standard item - read-only, never sent in a
  // create/update payload. Hard lock, no Active-style escape valve
  // (unlike Pet reference data), by design.
  isSystem?: boolean;
}

export interface VetReferenceUsage {
  vets: Record<number, number>;
}

export interface Vet {
  id: number;
  name: string;
  phone: string;
  email?: string;
  address?: string;
  isDeleted?: boolean;
  deletedAt?: string;
}

export interface Booking {
  id: number;
  clientId: number;
  client?: Client;
  // Full name and warning flag from the booking response, so the booking
  // page can show them without loading the client record.
  clientName?: string;
  clientShowWarning?: boolean;
  petId: number;
  pet?: Pet;
  checkInDate: string;
  checkOutDate: string;
  dailyRate: number;
  totalAmount: number;
  status: string;
  notes?: string;
  depositAmount?: number;
  createdAt?: string;
  rowVersion?: string;
  isDeleted?: boolean;
  // Set on retained quotes that have been converted to a real booking.
  // QuoteDetailCard renders a "Converted to Booking #N on <date>" line
  // and hides the convert/edit actions when this is set.
  convertedToBookingId?: number | null;
  convertedAt?: string | null;
  quoteExpiresAt?: string | null;
}

export interface BookingPetListItem {
  petId: number;
  petName: string;
  petType: string;
  // True for prospect/quote sketch pets. Hides the "View" link in the
  // bookings list since sketch pets are only editable via the quote form.
  isSketch?: boolean;
}

export interface BookingListItem {
  id: number;
  clientId: number;
  clientName: string;
  clientSurname: string;
  clientPhone: string;
  clientEmail: string;
  clientNotes: string;
  clientShowWarning: boolean;
  clientIsProspect: boolean;
  checkInDate: string;
  checkOutDate: string;
  numberOfNights: number;
  numberOfPets: number;
  totalAmount: number;
  status: string;
  createdAt: string;
  notes?: string;
  // ISO 8601. Only populated when status === "Quotation". The bookings
  // list uses it to render an "Expires"/"Expired" row.
  quoteExpiresAt?: string | null;
  // Set on retained quotes that have been converted to a real booking.
  // The bookings/quotes list shows a "Converted" chip when this is set.
  convertedToBookingId?: number | null;
  convertedAt?: string | null;
  pets: BookingPetListItem[];
}

export interface BookingGridKennelItem {
  kennelId: number;
  kennelNumber: number;
  kennelSideId: number;
  kennelSideName: string;
}

export interface BookingGridItem {
  id: number;
  clientId: number;
  clientName: string;
  clientSurname: string;
  checkInDate: string;
  checkOutDate: string;
  status: string;
  kennels: BookingGridKennelItem[];
}

export interface KennelSide {
  id: number;
  name: string;
  kennelCount?: number;
  // false for AnimalRoom (no individual kennels) / CatteryCommunal
  // (no heaters) respectively.
  hasKennels?: boolean;
  hasHeaters?: boolean;
  // Pet types housed on this side. Editable on system sides too.
  allowedPetTypeIds?: number[];
  isDeleted?: boolean;
  deletedAt?: string;
  // True for every seeded/standard item - read-only, never sent in a
  // create/update payload. Hard lock, no Active-style escape valve
  // (unlike Pet reference data), by design.
  isSystem?: boolean;
}

export interface KennelSideOption {
  id: number;
  displayName: string;
  kennelSideId: number;
  size: string | null;
  allowedPetTypeIds?: number[];
}

export interface Kennel {
  id: number;
  kennelNumber: number;
  kennelName?: string;
  size: string;
  kennelSizeId: number;
  kennelSideId: number;
  kennelSideName?: string;
  // True for every seeded/standard physical kennel - read-only, never
  // sent in a create/update payload. KennelNumber/KennelSizeId/
  // KennelSideId are all locked for these. Active is the escape valve -
  // a system kennel can still be taken out of rotation without deleting
  // it or needing a migration.
  isSystem?: boolean;
  active?: boolean;
}

export interface KennelGrouped {
  kennelSide: {
    id: number;
    name: string;
  };
  kennels: {
    id: number;
    kennelNumber: number;
    kennelName?: string;
    size: string;
    kennelSizeId: number;
    kennelSideId: number;
  }[];
  kennelCount: number;
}

export interface SystemSettings {
  id: number;
  heaterDailyRate: number;
  heaterDailyRate_LastUpdated?: string;
  showResolutionIndicator: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface UserDto {
  id: number;
  username: string;
  email: string;
  role: 'Admin' | 'Manager' | 'Staff';
  isActive: boolean;
  createdAt: string;
  lastLoginAt?: string;
  failedLoginCount: number;
  lockoutUntil?: string;
}

// ═══════════════════════════════════════════════════════════
// Clients API
// ═══════════════════════════════════════════════════════════

export const clientsAPI = {
  getAll: async (): Promise<Client[]> => {
    const response = await api.get<PaginatedResult<Client>>('/Client', {
      params: { pageNumber: 1, pageSize: 100 },
    });
    return response.data.data;
  },

  getAllPaginated: async (
    params: PaginationParams
  ): Promise<PaginatedResult<Client>> => {
    const response = await api.get<PaginatedResult<Client>>('/Client', {
      params: {
        pageNumber: params.pageNumber || 1,
        pageSize: params.pageSize || 100,
        letterFilter: params.letterFilter,
        searchText: params.searchText,
        hasPets: params.hasPets,
        hasBookings: params.hasBookings,
        hasOverdueInvoices: params.hasOverdueInvoices,
        hasOutstandingBalance: params.hasOutstandingBalance,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
        openId: params.openId,
      },
    });
    return response.data;
  },

  getById: async (id: number): Promise<Client> => {
    const response = await api.get<Client>(`/Client/${id}`);
    return response.data;
  },

  create: async (
    client: Omit<Client, 'id' | 'createdAt' | 'updatedAt' | 'isDeleted'>
  ): Promise<Client> => {
    const response = await api.post<Client>('/Client', client);
    return response.data;
  },

  update: async (id: number, client: Partial<Client>): Promise<Client> => {
    const response = await api.put<Client>(`/Client/${id}`, client);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/Client/${id}`);
  },
};

// ═══════════════════════════════════════════════════════════
// Pets API
// ═══════════════════════════════════════════════════════════

export const petsAPI = {
  getAllPaginated: async (
    params: PaginationParams
  ): Promise<PaginatedResult<Pet>> => {
    const transformedParams = {
      ...params,
      clientIds: params.clientIds?.length
        ? params.clientIds.join(',')
        : undefined,
      petTypeIds: params.petTypeIds?.length
        ? params.petTypeIds.join(',')
        : undefined,
    };
    const response = await api.get<PaginatedResult<Pet>>('/Pets', {
      params: transformedParams,
    });
    return response.data;
  },

  getById: async (id: number): Promise<Pet> => {
    const response = await api.get<Pet>(`/Pets/${id}`);
    return response.data;
  },

  getByClient: async (clientId: number): Promise<Pet[]> => {
    const response = await api.get<PaginatedResult<Pet>>(
      `/Pets/ByClient/${clientId}`,
      {
        params: { pageNumber: 1, pageSize: 1000 },
      }
    );
    return response.data.data;
  },

  getByClientPaginated: async (
    clientId: number,
    params: PaginationParams
  ): Promise<PaginatedResult<Pet>> => {
    const response = await api.get<PaginatedResult<Pet>>(
      `/Pets/ByClient/${clientId}`,
      { params }
    );
    return response.data;
  },

  create: async (
    pet: Omit<Pet, 'id' | 'createdAt' | 'updatedAt' | 'isDeleted'>
  ): Promise<Pet> => {
    const response = await api.post<Pet>('/Pets', pet);
    return response.data;
  },

  update: async (id: number, pet: Partial<Pet>): Promise<Pet> => {
    const response = await api.put<Pet>(`/Pets/${id}`, pet);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/Pets/${id}`);
  },
};

// ═══════════════════════════════════════════════════════════
// Reference Data APIs
// ═══════════════════════════════════════════════════════════

export const referenceDataAPI = {
  getPetTypes: async (): Promise<PetType[]> => {
    const response = await api.get<PetType[]>('/ReferenceData/PetTypes');
    return response.data;
  },
  getPetSizes: async (): Promise<PetSize[]> => {
    const response = await api.get<PetSize[]>('/ReferenceData/PetSizes');
    return response.data;
  },
  getPetSizesByType: async (petTypeId: number): Promise<PetSize[]> => {
    const response = await api.get<PetSize[]>(
      `/ReferenceData/PetSizes/ByType/${petTypeId}`
    );
    return response.data;
  },
  getPetBreeds: async (): Promise<PetBreed[]> => {
    const response = await api.get<PetBreed[]>('/ReferenceData/PetBreeds');
    return response.data;
  },
  // Breeds scoped to Type only (Size-unfiltered), active rows only - server
  // side filtered. Used by the Breed "Show All" toggle, which drops Size
  // scoping but keeps the pet's Type (see the "Show All" toggle rendered
  // as the last option in PetForm's/QuoteForm's Breed CustomSelect).
  getPetBreedsByType: async (petTypeId: number): Promise<PetBreed[]> => {
    const response = await api.get<PetBreed[]>(
      `/ReferenceData/PetBreeds/ByType/${petTypeId}`
    );
    return response.data;
  },
  getPetBreedsBySize: async (petSizeId: number): Promise<PetBreed[]> => {
    const response = await api.get<PetBreed[]>(
      `/ReferenceData/PetBreeds/BySize/${petSizeId}`
    );
    return response.data;
  },
  getPetBreedsByTypeAndSize: async (
    petTypeId: number,
    petSizeId: number
  ): Promise<PetBreed[]> => {
    const response = await api.get<PetBreed[]>(
      `/ReferenceData/PetBreeds/ByTypeAndSize/${petTypeId}/${petSizeId}`
    );
    return response.data;
  },
  getPetColours: async (): Promise<PetColour[]> => {
    const response = await api.get<PetColour[]>('/ReferenceData/PetColours');
    return response.data;
  },
  getPetColoursByType: async (petTypeId: number): Promise<PetColour[]> => {
    const response = await api.get<PetColour[]>(
      `/ReferenceData/PetColours/ByType/${petTypeId}`
    );
    return response.data;
  },
  getPetReferenceUsage: async (): Promise<PetReferenceUsage> => {
    const response = await api.get<PetReferenceUsage>(
      '/ReferenceData/PetReferenceUsage'
    );
    return response.data;
  },
  getVetReferenceUsage: async (): Promise<VetReferenceUsage> => {
    const response = await api.get<VetReferenceUsage>(
      '/ReferenceData/VetReferenceUsage'
    );
    return response.data;
  },
  getVets: async (): Promise<Vet[]> => {
    const response = await api.get<Vet[]>('/ReferenceData/Vets');
    return response.data;
  },
  createVet: async (vet: {
    name: string;
    phone: string;
    email?: string;
    address?: string;
  }): Promise<Vet> => {
    const response = await api.post<Vet>('/ReferenceData/Vets', vet);
    return response.data;
  },
  updateVet: async (id: number, vet: Partial<Vet>): Promise<Vet> => {
    const response = await api.put<Vet>(`/ReferenceData/Vets/${id}`, vet);
    return response.data;
  },
  deleteVet: async (id: number): Promise<void> => {
    await api.delete(`/ReferenceData/Vets/${id}`);
  },
  createPetType: async (data: {
    type: string;
    active?: boolean;
  }): Promise<PetType> => {
    const response = await api.post<PetType>('/ReferenceData/PetTypes', data);
    return response.data;
  },
  updatePetType: async (
    id: number,
    data: Partial<PetType>
  ): Promise<PetType> => {
    const response = await api.put<PetType>(
      `/ReferenceData/PetTypes/${id}`,
      data
    );
    return response.data;
  },
  deletePetType: async (id: number): Promise<void> => {
    await api.delete(`/ReferenceData/PetTypes/${id}`);
  },
  createPetSize: async (data: Partial<PetSize>): Promise<PetSize> => {
    const response = await api.post<PetSize>('/ReferenceData/PetSizes', data);
    return response.data;
  },
  updatePetSize: async (
    id: number,
    data: Partial<PetSize>
  ): Promise<PetSize> => {
    const response = await api.put<PetSize>(
      `/ReferenceData/PetSizes/${id}`,
      data
    );
    return response.data;
  },
  deletePetSize: async (id: number): Promise<void> => {
    await api.delete(`/ReferenceData/PetSizes/${id}`);
  },
  createPetBreed: async (data: {
    breed: string;
    petTypeId: number;
    petSizeFk?: number;
    active?: boolean;
  }): Promise<PetBreed> => {
    const response = await api.post<PetBreed>('/ReferenceData/PetBreeds', data);
    return response.data;
  },
  updatePetBreed: async (
    id: number,
    data: Partial<PetBreed & { petTypeId: number }>
  ): Promise<PetBreed> => {
    const response = await api.put<PetBreed>(
      `/ReferenceData/PetBreeds/${id}`,
      data
    );
    return response.data;
  },
  deletePetBreed: async (id: number): Promise<void> => {
    await api.delete(`/ReferenceData/PetBreeds/${id}`);
  },
  createPetColour: async (data: {
    colour: string;
    petTypeId: number;
    active?: boolean;
  }): Promise<PetColour> => {
    const response = await api.post<PetColour>(
      '/ReferenceData/PetColours',
      data
    );
    return response.data;
  },
  updatePetColour: async (
    id: number,
    data: Partial<PetColour & { petTypeId: number }>
  ): Promise<PetColour> => {
    const response = await api.put<PetColour>(
      `/ReferenceData/PetColours/${id}`,
      data
    );
    return response.data;
  },
  deletePetColour: async (id: number): Promise<void> => {
    await api.delete(`/ReferenceData/PetColours/${id}`);
  },
  getPricingInfo: async (petSizeId: number): Promise<PetSize> => {
    const response = await api.get<PetSize>(
      `/ReferenceData/PricingInfo/BySize/${petSizeId}`
    );
    return response.data;
  },
  getSystemSettings: async (): Promise<SystemSettings> => {
    const response = await api.get<SystemSettings>(
      '/ReferenceData/system-settings'
    );
    return response.data;
  },
  updateSystemSettings: async (
    settings: Partial<SystemSettings>
  ): Promise<SystemSettings> => {
    const response = await api.put<SystemSettings>(
      '/ReferenceData/system-settings',
      settings
    );
    return response.data;
  },
};

// ═══════════════════════════════════════════════════════════
// Bookings API
// ═══════════════════════════════════════════════════════════

export const bookingsAPI = {
  getDashboardStats: async (date?: string): Promise<DashboardStats> => {
    const response = await api.get<DashboardStats>('/Booking/dashboard-stats', {
      params: date ? { date } : undefined,
    });
    return response.data;
  },

  getAllPaginated: async (
    params: PaginationParams
  ): Promise<PaginatedResult<BookingListItem>> => {
    const response = await api.get<PaginatedResult<BookingListItem>>(
      '/Booking',
      {
        params: {
          pageNumber: params.pageNumber || 1,
          pageSize: params.pageSize || 100,
          searchText: params.searchText,
          clientId: params.clientId,
          status: params.status,
          startDate: params.startDate,
          endDate: params.endDate,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
          isProspect: params.isProspect,
          isExpired: params.isExpired,
          openId: params.openId,
        },
      }
    );
    return response.data;
  },

  getAll: async (filters?: {
    clientId?: number;
    status?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<BookingListItem[]> => {
    const response = await api.get<PaginatedResult<BookingListItem>>(
      '/Booking',
      {
        params: { pageNumber: 1, pageSize: 1000, ...filters },
      }
    );
    return response.data.data;
  },

  getGrid: async (
    startDate: string,
    endDate: string
  ): Promise<BookingGridItem[]> => {
    const response = await api.get<BookingGridItem[]>('/Booking/grid', {
      params: { startDate, endDate },
    });
    return response.data;
  },

  getById: async (id: number): Promise<Booking> => {
    const response = await api.get<Booking>(`/Booking/${id}`);
    return response.data;
  },

  create: async (
    booking: Omit<Booking, 'id' | 'createdAt' | 'isDeleted'>
  ): Promise<Booking> => {
    const response = await api.post<Booking>('/Booking', booking);
    return response.data;
  },

  update: async (id: number, booking: Partial<Booking>): Promise<Booking> => {
    const response = await api.put<Booking>(`/Booking/${id}`, booking);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/Booking/${id}`);
  },

  sendBookingForm: async (bookingId: number): Promise<void> => {
    await api.post(`/Booking/${bookingId}/send-booking-form`);
  },

  getBookingFormLog: async (
    bookingId: number
  ): Promise<BookingFormEmailLogEntry[]> => {
    const response = await api.get<BookingFormEmailLogEntry[]>(
      `/Booking/${bookingId}/booking-form-log`
    );
    return response.data;
  },
};

// ═══════════════════════════════════════════════════════════
// Invoice & Payment types
// ═══════════════════════════════════════════════════════════

export interface InvoiceSummary {
  id: number;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  bookingId: number;
  subTotal: number;
  taxAmount: number;
  totalAmount: number;
  paidAmount: number;
  outstandingAmount: number;
  status: 'Pending' | 'Paid' | 'Overdue' | 'Cancelled';
}

export interface OverdueInvoiceItem {
  id: number;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  bookingId: number;
  clientId: number;
  clientName: string;
  totalAmount: number;
  paidAmount: number;
  outstandingAmount: number;
  daysOverdue: number;
}

export interface LedgerEntry {
  date: string;
  type: string;
  description: string;
  bookingRef?: number;
  transactionReference?: string;
  debit: number;
  credit: number;
  balance: number;
}

export interface AccountSummary {
  clientId: number;
  clientName: string;
  totalInvoiced: number;
  totalPaid: number;
  outstandingBalance: number;
  entries: LedgerEntry[];
}

export interface PaymentRecord {
  id: number;
  clientId: number;
  invoiceId: number | null;
  amount: number;
  isDebit: boolean;
  paymentDate: string;
  paymentMethod: string;
  transactionReference: string;
  notes: string;
  createdAt: string;
}

export interface InvoiceEmailLogEntry {
  id: number;
  sentAt: string;
  sentByUsername: string;
  recipientEmail: string;
  success: boolean;
  errorMessage?: string;
}

export interface QuoteEmailLogEntry {
  id: number;
  sentAt: string;
  sentByUsername: string;
  recipientEmail: string;
  success: boolean;
  errorMessage?: string;
  channel: string;
}

export interface BookingFormEmailLogEntry {
  id: number;
  sentAt: string;
  sentByUsername: string;
  recipientEmail: string;
  success: boolean;
  errorMessage?: string;
  channel: string;
}

export interface CreatePaymentRequest {
  invoiceId: number | null;
  // Only used (and only required by the backend) when invoiceId is null -
  // an adjustment-category payment with no invoice to derive a client from.
  clientId?: number;
  amount: number;
  paymentDate?: string;
  paymentMethod:
    | 'Cash'
    | 'Card'
    | 'EFT'
    | 'Deposit'
    | 'Credit'
    | 'Refund'
    | 'Journal'
    | 'OpeningBalance';
  // Only used (and only required by the backend) for Journal and
  // OpeningBalance - both genuinely bidirectional, unlike every other
  // method whose direction is a fixed accounting fact.
  isDebit?: boolean;
  transactionReference?: string;
  notes?: string;
}

// ═══════════════════════════════════════════════════════════
// Quotes API
// ═══════════════════════════════════════════════════════════

export const quotesAPI = {
  createQuick: async (payload: QuoteRequest): Promise<QuoteResponse> => {
    const response = await api.post<QuoteResponse>('/quotes/quick', payload);
    return response.data;
  },
  createDetailed: async (
    payload: QuoteDetailedRequest
  ): Promise<QuoteResponse> => {
    const response = await api.post<QuoteResponse>('/quotes/detailed', payload);
    return response.data;
  },
  updateQuick: async (
    bookingId: number,
    payload: QuoteRequest
  ): Promise<QuoteResponse> => {
    const response = await api.put<QuoteResponse>(
      `/quotes/${bookingId}/quick`,
      payload
    );
    return response.data;
  },
  updateDetailed: async (
    bookingId: number,
    payload: QuoteDetailedRequest
  ): Promise<QuoteResponse> => {
    const response = await api.put<QuoteResponse>(
      `/quotes/${bookingId}/detailed`,
      payload
    );
    return response.data;
  },
  convert: async (
    bookingId: number,
    payload: ConvertQuoteRequest
  ): Promise<ConvertQuoteResponse> => {
    const response = await api.post<ConvertQuoteResponse>(
      `/quotes/${bookingId}/convert`,
      payload
    );
    return response.data;
  },
  downloadPdf: async (bookingId: number): Promise<Blob> => {
    const response = await api.get(`/quotes/${bookingId}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },
  emailQuote: async (
    bookingId: number,
    includeBookingForm: boolean = true
  ): Promise<void> => {
    await api.post(
      `/quotes/${bookingId}/email?includeBookingForm=${includeBookingForm}`
    );
  },
  getEmailLog: async (bookingId: number): Promise<QuoteEmailLogEntry[]> => {
    const response = await api.get<QuoteEmailLogEntry[]>(
      `/quotes/${bookingId}/email-log`
    );
    return response.data;
  },
};

// ═══════════════════════════════════════════════════════════
// Invoice API
// ═══════════════════════════════════════════════════════════

export const invoiceAPI = {
  getOverdue: async (date?: string): Promise<OverdueInvoiceItem[]> => {
    const response = await api.get<OverdueInvoiceItem[]>('/Invoice/overdue', {
      params: date ? { date } : undefined,
    });
    return response.data;
  },
  getByClient: async (clientId: number): Promise<InvoiceSummary[]> => {
    const response = await api.get<InvoiceSummary[]>(
      `/Invoice/client/${clientId}`
    );
    return response.data;
  },
  getByBooking: async (bookingId: number): Promise<InvoiceSummary[]> => {
    const response = await api.get<InvoiceSummary[]>(
      `/Invoice/booking/${bookingId}`
    );
    return response.data;
  },
  getById: async (id: number): Promise<InvoiceSummary> => {
    const response = await api.get<InvoiceSummary>(`/Invoice/${id}`);
    return response.data;
  },
  downloadPdf: async (id: number): Promise<Blob> => {
    const response = await api.get(`/Invoice/${id}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },
  emailInvoice: async (id: number): Promise<void> => {
    await api.post(`/Invoice/${id}/email`);
  },
  getEmailLog: async (invoiceId: number): Promise<InvoiceEmailLogEntry[]> => {
    const response = await api.get<InvoiceEmailLogEntry[]>(
      `/Invoice/${invoiceId}/email-log`
    );
    return response.data;
  },
  getAccountSummary: async (clientId: number): Promise<AccountSummary> => {
    const response = await api.get<AccountSummary>(
      `/Invoice/client/${clientId}/account-summary`
    );
    return response.data;
  },
};

// ═══════════════════════════════════════════════════════════
// Payment API
// ═══════════════════════════════════════════════════════════

export const paymentAPI = {
  getByInvoice: async (invoiceId: number): Promise<PaymentRecord[]> => {
    const response = await api.get<PaymentRecord[]>(
      `/Payment/invoice/${invoiceId}`
    );
    return response.data;
  },
  getById: async (id: number): Promise<PaymentRecord> => {
    const response = await api.get<PaymentRecord>(`/Payment/${id}`);
    return response.data;
  },
  create: async (request: CreatePaymentRequest): Promise<PaymentRecord> => {
    const response = await api.post<PaymentRecord>('/Payment', request);
    return response.data;
  },
  update: async (
    id: number,
    request: Partial<CreatePaymentRequest>
  ): Promise<PaymentRecord> => {
    const response = await api.put<PaymentRecord>(`/Payment/${id}`, request);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/Payment/${id}`);
  },
};

// ═══════════════════════════════════════════════════════════
// Kennels API
// ═══════════════════════════════════════════════════════════

export const kennelsAPI = {
  getGrouped: async (): Promise<KennelGrouped[]> => {
    const response = await api.get<KennelGrouped[]>('/Kennel/grouped');
    return response.data;
  },
  getSideOptions: async (): Promise<KennelSideOption[]> => {
    const response = await api.get<KennelSideOption[]>('/Kennel/side-options');
    return response.data;
  },
  getSides: async (): Promise<KennelSide[]> => {
    const response = await api.get<KennelSide[]>('/Kennel/sides');
    return response.data;
  },
  getAll: async (): Promise<Kennel[]> => {
    const response = await api.get<Kennel[]>('/Kennel');
    return response.data;
  },
  create: async (data: {
    kennelNumber: number;
    kennelName?: string;
    kennelSizeId: number;
    kennelSideId: number;
  }): Promise<Kennel> => {
    const response = await api.post<Kennel>('/Kennel', data);
    return response.data;
  },
  update: async (id: number, data: Partial<Kennel>): Promise<Kennel> => {
    const response = await api.put<Kennel>(`/Kennel/${id}`, data);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/Kennel/${id}`);
  },
  createSide: async (data: {
    name: string;
    hasKennels?: boolean;
    hasHeaters?: boolean;
    allowedPetTypeIds?: number[];
  }): Promise<KennelSide> => {
    const response = await api.post<KennelSide>('/Kennel/sides', data);
    return response.data;
  },
  updateSide: async (
    id: number,
    data: Partial<KennelSide>
  ): Promise<KennelSide> => {
    const response = await api.put<KennelSide>(`/Kennel/sides/${id}`, data);
    return response.data;
  },
  deleteSide: async (id: number): Promise<void> => {
    await api.delete(`/Kennel/sides/${id}`);
  },
  getSizes: async (): Promise<KennelSize[]> => {
    const response = await api.get<KennelSize[]>('/Kennel/sizes');
    return response.data;
  },
  createSize: async (data: {
    name: string;
    sortOrder?: number;
  }): Promise<KennelSize> => {
    const response = await api.post<KennelSize>('/Kennel/sizes', data);
    return response.data;
  },
  updateSize: async (
    id: number,
    data: Partial<KennelSize>
  ): Promise<KennelSize> => {
    const response = await api.put<KennelSize>(`/Kennel/sizes/${id}`, data);
    return response.data;
  },
  deleteSize: async (id: number): Promise<void> => {
    await api.delete(`/Kennel/sizes/${id}`);
  },
  getReferenceUsage: async (): Promise<KennelReferenceUsage> => {
    const response = await api.get<KennelReferenceUsage>(
      '/Kennel/reference-usage'
    );
    return response.data;
  },
};

// ═══════════════════════════════════════════════════════════
// Users API
// ═══════════════════════════════════════════════════════════

export const usersAPI = {
  getAll: async (): Promise<UserDto[]> => {
    const response = await api.get<UserDto[]>('/User');
    return response.data;
  },
  getById: async (id: number): Promise<UserDto> => {
    const response = await api.get<UserDto>(`/User/${id}`);
    return response.data;
  },
  create: async (data: {
    username: string;
    email: string;
    password: string;
    role: string;
  }): Promise<UserDto> => {
    const response = await api.post<UserDto>('/User', data);
    return response.data;
  },
  update: async (
    id: number,
    data: {
      username?: string;
      email?: string;
      password?: string;
      role?: string;
      isActive?: boolean;
    }
  ): Promise<void> => {
    await api.put(`/User/${id}`, data);
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/User/${id}`);
  },
  unlock: async (id: number): Promise<void> => {
    await api.post(`/User/${id}/unlock`);
  },
};

// ═══════════════════════════════════════════════════════════
// Performance Metrics API
// ═══════════════════════════════════════════════════════════

export const performanceAPI = {
  getMetrics: async (
    pageNumber = 1,
    pageSize = 100,
    endpoint?: string,
    fromDate?: Date,
    toDate?: Date
  ): Promise<PerformanceMetricsResponse> => {
    const params = new URLSearchParams();
    params.append('pageNumber', pageNumber.toString());
    params.append('pageSize', pageSize.toString());
    if (endpoint) params.append('endpoint', endpoint);
    if (fromDate) params.append('fromDate', fromDate.toISOString());
    if (toDate) params.append('toDate', toDate.toISOString());
    const response = await api.get<PerformanceMetricsResponse>(
      `/PerformanceMetrics?${params.toString()}`
    );
    return response.data;
  },
  getStats: async (
    fromDate?: Date,
    toDate?: Date
  ): Promise<PerformanceStatsResponse> => {
    const params = new URLSearchParams();
    if (fromDate) params.append('fromDate', fromDate.toISOString());
    if (toDate) params.append('toDate', toDate.toISOString());
    const response = await api.get<PerformanceStatsResponse>(
      `/PerformanceMetrics/stats?${params.toString()}`
    );
    return response.data;
  },
  getMetricsByEndpoint: async (endpoint: string, limit = 100): Promise<any> => {
    const response = await api.get(
      `/PerformanceMetrics/endpoint/${encodeURIComponent(endpoint)}?limit=${limit}`
    );
    return response.data;
  },
  cleanupOldMetrics: async (olderThanDays = 30): Promise<any> => {
    const response = await api.delete(
      `/PerformanceMetrics/cleanup?olderThanDays=${olderThanDays}`
    );
    return response.data;
  },
};

// ═══════════════════════════════════════════════════════════
// Recycle Bin API
// ═══════════════════════════════════════════════════════════

export interface DeletedClient {
  id: number;
  firstName: string;
  lastName: string;
  deletedAt: string;
  petCount: number;
  bookingCount: number;
}

export interface DeletedPet {
  id: number;
  name: string;
  petType: string;
  clientName: string;
  clientId: number;
  clientIsDeleted: boolean;
  deletedAt: string;
}

export interface DeletedBooking {
  id: number;
  clientName: string;
  clientId: number;
  clientIsDeleted: boolean;
  checkInDate: string;
  checkOutDate: string;
  status: string;
  totalAmount: number;
  deletedAt: string;
}

export interface DeletedUser {
  id: number;
  username: string;
  email: string;
  role: string;
  deletedAt: string;
}

export const recycleBinAPI = {
  getDeletedClients: async (): Promise<DeletedClient[]> => {
    const response = await api.get<DeletedClient[]>('/Client/deleted');
    return response.data;
  },
  getDeletedPets: async (): Promise<DeletedPet[]> => {
    const response = await api.get<DeletedPet[]>('/Pets/deleted');
    return response.data;
  },
  getDeletedBookings: async (): Promise<DeletedBooking[]> => {
    const response = await api.get<DeletedBooking[]>('/Booking/deleted');
    return response.data;
  },
  restoreClient: async (id: number): Promise<void> => {
    await api.put(`/Client/${id}/restore`);
  },
  restorePet: async (id: number): Promise<void> => {
    await api.put(`/Pets/${id}/restore`);
  },
  restoreBooking: async (id: number): Promise<void> => {
    await api.put(`/Booking/${id}/restore`);
  },
  permanentDeleteClient: async (id: number): Promise<void> => {
    await api.delete(`/Client/${id}/permanent`);
  },
  permanentDeletePet: async (id: number): Promise<void> => {
    await api.delete(`/Pets/${id}/permanent`);
  },
  permanentDeleteBooking: async (id: number): Promise<void> => {
    await api.delete(`/Booking/${id}/permanent`);
  },

  // Users
  getDeletedUsers: async (): Promise<DeletedUser[]> => {
    const response = await api.get<DeletedUser[]>('/User/deleted');
    return response.data;
  },
  restoreUser: async (id: number): Promise<void> => {
    await api.put(`/User/${id}/restore`);
  },
  permanentDeleteUser: async (id: number): Promise<void> => {
    await api.delete(`/User/${id}/permanent`);
  },

  // Vets
  getDeletedVets: async (): Promise<Vet[]> => {
    const response = await api.get<Vet[]>('/ReferenceData/Vets/deleted');
    return response.data;
  },
  restoreVet: async (id: number): Promise<void> => {
    await api.put(`/ReferenceData/Vets/${id}/restore`);
  },
  permanentDeleteVet: async (id: number): Promise<void> => {
    await api.delete(`/ReferenceData/Vets/${id}/permanent`);
  },

  // Pet Types
  getDeletedPetTypes: async (): Promise<PetType[]> => {
    const response = await api.get<PetType[]>(
      '/ReferenceData/PetTypes/deleted'
    );
    return response.data;
  },
  restorePetType: async (id: number): Promise<void> => {
    await api.put(`/ReferenceData/PetTypes/${id}/restore`);
  },
  permanentDeletePetType: async (id: number): Promise<void> => {
    await api.delete(`/ReferenceData/PetTypes/${id}/permanent`);
  },

  // Pet Sizes
  getDeletedPetSizes: async (): Promise<PetSize[]> => {
    const response = await api.get<PetSize[]>(
      '/ReferenceData/PetSizes/deleted'
    );
    return response.data;
  },
  restorePetSize: async (id: number): Promise<void> => {
    await api.put(`/ReferenceData/PetSizes/${id}/restore`);
  },
  permanentDeletePetSize: async (id: number): Promise<void> => {
    await api.delete(`/ReferenceData/PetSizes/${id}/permanent`);
  },

  // Pet Breeds
  getDeletedPetBreeds: async (): Promise<PetBreed[]> => {
    const response = await api.get<PetBreed[]>(
      '/ReferenceData/PetBreeds/deleted'
    );
    return response.data;
  },
  restorePetBreed: async (id: number): Promise<void> => {
    await api.put(`/ReferenceData/PetBreeds/${id}/restore`);
  },
  permanentDeletePetBreed: async (id: number): Promise<void> => {
    await api.delete(`/ReferenceData/PetBreeds/${id}/permanent`);
  },

  // Pet Colours
  getDeletedPetColours: async (): Promise<PetColour[]> => {
    const response = await api.get<PetColour[]>(
      '/ReferenceData/PetColours/deleted'
    );
    return response.data;
  },
  restorePetColour: async (id: number): Promise<void> => {
    await api.put(`/ReferenceData/PetColours/${id}/restore`);
  },
  permanentDeletePetColour: async (id: number): Promise<void> => {
    await api.delete(`/ReferenceData/PetColours/${id}/permanent`);
  },

  // Kennel Sizes
  getDeletedKennelSizes: async (): Promise<KennelSize[]> => {
    const response = await api.get<KennelSize[]>('/Kennel/sizes/deleted');
    return response.data;
  },
  restoreKennelSize: async (id: number): Promise<void> => {
    await api.put(`/Kennel/sizes/${id}/restore`);
  },
  permanentDeleteKennelSize: async (id: number): Promise<void> => {
    await api.delete(`/Kennel/sizes/${id}/permanent`);
  },

  // Kennel Sides
  getDeletedKennelSides: async (): Promise<KennelSide[]> => {
    const response = await api.get<KennelSide[]>('/Kennel/sides/deleted');
    return response.data;
  },
  restoreKennelSide: async (id: number): Promise<void> => {
    await api.put(`/Kennel/sides/${id}/restore`);
  },
  permanentDeleteKennelSide: async (id: number): Promise<void> => {
    await api.delete(`/Kennel/sides/${id}/permanent`);
  },
};

// ═══════════════════════════════════════════════════════════
// Activity Log API
// ═══════════════════════════════════════════════════════════

export interface ActivityLogEntry {
  id: number;
  userId: number;
  username: string;
  action: string;
  entityType: string;
  entityId: number | null;
  description: string;
  oldValue: string | null;
  newValue: string | null;
  ipAddress: string | null;
  createdAt: string;
}

export interface ActivityLogPagedResult {
  items: ActivityLogEntry[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface ActivityLogParams {
  pageNumber?: number;
  pageSize?: number;
  username?: string;
  action?: string;
  entityType?: string;
  fromDate?: string;
  toDate?: string;
}

export const activityLogAPI = {
  getLogs: async (
    params: ActivityLogParams = {}
  ): Promise<ActivityLogPagedResult> => {
    const response = await api.get<ActivityLogPagedResult>('/ActivityLogs', {
      params,
    });
    return response.data;
  },
};

export const authAPI = {
  logout: async (): Promise<void> => {
    await api.post('/Auth/logout');
  },
  forgotPassword: async (usernameOrEmail: string): Promise<void> => {
    await api.post('/Auth/forgot-password', { usernameOrEmail });
  },
  resetPassword: async (token: string, newPassword: string): Promise<void> => {
    await api.post('/Auth/reset-password', { token, newPassword });
  },
};

export interface ActivityLogRetentionStatus {
  retentionDays: number;
  sweepIntervalHours: number;
  lastSweepAt: string | null;
  lastSweepDeletedCount: number | null;
  nextSweepAt: string | null;
  expiredCount: number;
}

export const activityLogRetentionAPI = {
  getStatus: async (): Promise<ActivityLogRetentionStatus> => {
    const response = await api.get<ActivityLogRetentionStatus>(
      '/ActivityLogRetention/status'
    );
    return response.data;
  },
  sweepNow: async (): Promise<{ deleted: number }> => {
    const response = await api.post<{ deleted: number }>(
      '/ActivityLogRetention/sweep'
    );
    return response.data;
  },
};

export interface QuoteCleanupStatus {
  retentionDays: number;
  sweepIntervalHours: number;
  hardDeleteRetentionDays: number;
  lastSweepAt: string | null;
  lastSweepDeletedCount: number | null;
  nextSweepAt: string | null;
  lastHardDeleteAt: string | null;
  lastHardDeletedCount: number | null;
  expiredCount: number;
  softDeletedCount: number;
}

export const quoteCleanupAPI = {
  getStatus: async (): Promise<QuoteCleanupStatus> => {
    const response = await api.get<QuoteCleanupStatus>('/QuoteCleanup/status');
    return response.data;
  },
};

export interface BackupLogEntry {
  id: number;
  fileName: string;
  sizeBytes: number;
  durationMs: number;
  success: boolean;
  errorMessage: string | null;
  triggeredBy: string;
  createdAt: string;
}

export interface BackupStatus {
  enabled: boolean;
  intervalHours: number;
  retentionCount: number;
  lastBackupAt: string | null;
  nextBackupAt: string | null;
  recent: BackupLogEntry[];
}

export interface BackupRunResult {
  success: boolean;
  fileName: string | null;
  sizeBytes: number;
  durationMs: number;
  errorMessage: string | null;
}

export const backupAPI = {
  getStatus: async (): Promise<BackupStatus> => {
    const response = await api.get<BackupStatus>('/Backup/status');
    return response.data;
  },
  runNow: async (): Promise<BackupRunResult> => {
    const response = await api.post<BackupRunResult>('/Backup/run');
    return response.data;
  },
  download: async (id: number): Promise<Blob> => {
    const response = await api.get(`/Backup/download/${id}`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

export interface Notification {
  id: number;
  category: 'System' | 'Backup' | 'Quote' | 'Booking';
  title: string;
  body: string;
  isRead: boolean;
  readAt: string | null;
  expiresAt: string | null;
  createdAt: string;
  isBroadcast: boolean;
}

export interface MessagePreferences {
  msgBackupAlerts: boolean;
  msgQuoteAlerts: boolean;
  msgBookingReminders: boolean;
  msgSystemToasts: boolean;
  accessibilityMode: boolean;
  showPerformanceIndicator: boolean;
  showOnboarding: boolean;
  themeMode: 'light' | 'dark';
  locale: string;
}

export const notificationsAPI = {
  getAll: async (): Promise<Notification[]> => {
    const response = await api.get<Notification[]>('/Notifications');
    return response.data;
  },
  getUnreadCount: async (): Promise<number> => {
    const response = await api.get<{ count: number }>(
      '/Notifications/unread-count',
      { skipPerfMetric: true }
    );
    return response.data.count;
  },
  markRead: async (id: number): Promise<void> => {
    await api.put(`/Notifications/${id}/read`);
  },
  markAllRead: async (): Promise<void> => {
    await api.put('/Notifications/mark-all-read');
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/Notifications/${id}`);
  },
  deleteBulk: async (ids: number[]): Promise<void> => {
    await api.delete('/Notifications/bulk', { data: ids });
  },
  // category is intentionally not a parameter here - the backend hard-codes
  // "System" for every broadcast composed through this endpoint, so an admin
  // can never accidentally file a human-authored announcement under an
  // automated, opt-out-able category (e.g. "Backup").
  broadcast: async (
    title: string,
    body: string,
    expiresAt?: string
  ): Promise<void> => {
    await api.post('/Notifications/broadcast', {
      title,
      body,
      expiresAt,
    });
  },
};

export const userPreferencesAPI = {
  getMessagePreferences: async (options?: {
    background?: boolean;
  }): Promise<MessagePreferences> => {
    const response = await api.get<MessagePreferences>('/Profile/preferences', {
      skipPerfMetric: options?.background ?? false,
    });
    return response.data;
  },
  updateMessagePreferences: async (
    prefs: MessagePreferences
  ): Promise<void> => {
    await api.put('/Profile/preferences', prefs);
  },
  changePassword: async (
    currentPassword: string,
    newPassword: string
  ): Promise<void> => {
    await api.post('/Profile/change-password', {
      currentPassword,
      newPassword,
    });
  },
};

// ═══════════════════════════════════════════════════════════
// Version API
// ═══════════════════════════════════════════════════════════

export interface VersionInfo {
  app: string;
  backend: string;
}

export const versionAPI = {
  getVersion: async (): Promise<VersionInfo> => {
    const response = await api.get<VersionInfo>('/Version');
    return response.data;
  },
};

// ═══════════════════════════════════════════════════════════
// Web Vitals API
// ═══════════════════════════════════════════════════════════

export interface WebVitalMetric {
  id: number;
  metricName: string;
  value: number;
  rating: string;
  route: string;
  navigationType?: string;
  timestamp: string;
}

export interface WebVitalMetricsResponse {
  success: boolean;
  data: WebVitalMetric[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface WebVitalMetricSummary {
  metricName: string;
  sampleCount: number;
  p75Value: number;
  p75Rating: string;
  averageValue: number;
  goodCount: number;
  needsImprovementCount: number;
  poorCount: number;
}

export interface WebVitalStats {
  metrics: Record<string, WebVitalMetricSummary>;
}

export interface WebVitalStatsResponse {
  success: boolean;
  data: WebVitalStats;
}

export const webVitalsAPI = {
  recordMetric: async (
    metricName: string,
    value: number,
    rating: string,
    route: string,
    navigationType?: string
  ): Promise<void> => {
    await api.post(
      '/WebVitals',
      { metricName, value, rating, route, navigationType },
      { skipPerfMetric: true }
    );
  },
  getMetrics: async (
    pageNumber = 1,
    pageSize = 100,
    metricName?: string,
    fromDate?: Date,
    toDate?: Date
  ): Promise<WebVitalMetricsResponse> => {
    const params = new URLSearchParams();
    params.append('pageNumber', pageNumber.toString());
    params.append('pageSize', pageSize.toString());
    if (metricName) params.append('metricName', metricName);
    if (fromDate) params.append('fromDate', fromDate.toISOString());
    if (toDate) params.append('toDate', toDate.toISOString());
    const response = await api.get<WebVitalMetricsResponse>(
      `/WebVitals?${params.toString()}`
    );
    return response.data;
  },
  getStats: async (
    fromDate?: Date,
    toDate?: Date
  ): Promise<WebVitalStatsResponse> => {
    const params = new URLSearchParams();
    if (fromDate) params.append('fromDate', fromDate.toISOString());
    if (toDate) params.append('toDate', toDate.toISOString());
    const response = await api.get<WebVitalStatsResponse>(
      `/WebVitals/stats?${params.toString()}`
    );
    return response.data;
  },
};
